/* eslint-disable max-len */

/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable } from '@angular/core';
import * as d3 from 'd3';
import { DateTime } from 'luxon';

import { D3ChartSvgg, XAxisData } from '@shared/types/common-graph.types';

import { ChartXAxisConfig } from '../chart.view-model';
import {
  createXScale,
  getCompactDataPointLayout,
  getTickValueText,
  getXAxisBandwidth,
  getXScaleMinMaxValues,
} from '../helpers/x-scale.helper';

@Injectable()
export class XAxisService<Data extends object> {
  public createXAxis(
    data: Data[],
    config: ChartXAxisConfig<Data>,
    linesContainer: D3ChartSvgg,
    availableChartsAreaWidth: number,
    availableChartHeight: number,
    timeZone?: string
  ): XAxisData {
    const uniqueDataSet = new Set(
      data.map((d) => {
        const date = d[config.dataKey] as number | DateTime;

        if (typeof date === 'number') {
          return date;
        }

        return date.toMillis();
      })
    );
    const xAxisDataSet = [...uniqueDataSet.values()];
    xAxisDataSet.sort((a, b) => a - b);

    const [minX, maxX] = this.getXScaleDomain(xAxisDataSet, config);

    const x = createXScale(minX, maxX);
    const compactLayout = getCompactDataPointLayout(xAxisDataSet.length, availableChartsAreaWidth, config.compactDataPointLayout);
    const bandwidth = compactLayout?.bandwidth ?? getXAxisBandwidth(xAxisDataSet, minX, maxX, availableChartsAreaWidth);
    const verticalTexts = !config.hourMinutesTickFormat && (config.verticalTexts ?? xAxisDataSet.length > 7);

    x.range(compactLayout ? [compactLayout.start, compactLayout.end] : [0, availableChartsAreaWidth]);

    const xAxis = d3.axisBottom(x);
    xAxis.tickValues(config.tickValues ?? xAxisDataSet);

    const xAxisGHeight = config.hourMinutesTickFormat
      ? this.createHourMinutesAxisTickValues(linesContainer, xAxis, availableChartHeight, timeZone)
      : this.createNormalXAxisTickValues(config, linesContainer, xAxisDataSet, xAxis, availableChartHeight, verticalTexts, timeZone);

    return {
      scale: x,
      height: xAxisGHeight,
      axis: xAxis,
      tickValues: xAxisDataSet,
      bandwidth,
      verticalTexts,
    };
  }

  private getXScaleDomain(xAxisDataSet: number[], config: ChartXAxisConfig<Data>): [number, number] {
    if (config.compactDataPointLayout && xAxisDataSet.length > 0) {
      return [xAxisDataSet[0], xAxisDataSet.at(-1)!];
    }

    return getXScaleMinMaxValues(xAxisDataSet, config.edgePaddingRatio);
  }

  private createNormalXAxisTickValues(
    config: ChartXAxisConfig<Data>,
    linesContainer: D3ChartSvgg,
    xAxisDataSet: number[],
    xAxis: d3.Axis<Date | d3.NumberValue>,
    availableChartHeight: number,
    verticalTexts: boolean,
    timeZone?: string
  ): number {
    xAxis.tickFormat((d) => {
      return config.tickFormat
        ? config.tickFormat(d as number, timeZone)
        : getTickValueText(d as number, xAxisDataSet, verticalTexts, timeZone);
    });

    const xAxisG = linesContainer.append('g').attr('transform', `translate(0, ${availableChartHeight})`).call(xAxis);

    xAxisG.selectAll('.domain').remove();
    xAxisG.selectAll('.tick line').remove();

    xAxisG
      .selectAll('.tick text')
      .attr('class', 'fill-foreground-primary-60 text-[11px]')
      .attr('x', 0)
      .attr('text-anchor', 'middle')
      .attr('transform', verticalTexts ? 'rotate(-90)' : '');

    if (config.label) {
      this.addLabelToXAxis(xAxisG, config.label, config.labelTransform);
    }

    const xAxisGHeight = xAxisG.node()!.getBBox().height;
    xAxisG.attr(
      'transform',
      `translate(${verticalTexts ? '-14' : '0'}, ${Math.round(availableChartHeight - (verticalTexts ? xAxisGHeight / 2 - 6 : xAxisGHeight))})`
    );

    return xAxisGHeight;
  }

  private createHourMinutesAxisTickValues(
    linesContainer: D3ChartSvgg,
    xAxis: d3.Axis<Date | d3.NumberValue>,
    availableChartHeight: number,
    timeZone?: string
  ): number {
    xAxis.tickFormat((d) => {
      return `${d.valueOf()}`;
    });

    const xAxisG = linesContainer.append('g').attr('transform', `translate(0, ${availableChartHeight})`).call(xAxis);

    xAxisG.selectAll('.domain').remove();
    xAxisG.selectAll('.tick line').remove();
    xAxisG.selectAll('.tick text').attr('class', 'fill-foreground-primary-60 text-xs').attr('x', 0).attr('text-anchor', 'middle');

    const tickTexts = xAxisG.selectAll('.tick text').attr('class', `fill-foreground-primary-100 text-[11px]`);

    tickTexts.nodes().forEach((node) => {
      const textContent = d3.select(node).text();
      const dateTime = DateTime.fromMillis(Number.parseInt(textContent, 10), timeZone ? { zone: timeZone } : undefined);
      d3.select(node).text('');

      d3.select(node).insert('tspan').attr('x', 0).attr('text-anchor', 'middle').text(dateTime.toFormat('h'));
      d3.select(node)
        .insert('tspan')
        .attr('dy', '-0.2em')
        .attr('text-anchor', 'middle')
        .attr('class', `text-[8px]`)
        .text(dateTime.toFormat('a').toUpperCase());
    });

    const xAxisGHeight = xAxisG.node()!.getBBox().height;
    xAxisG.attr('transform', `translate(0, ${availableChartHeight - xAxisGHeight})`);

    return xAxisGHeight;
  }

  private addLabelToXAxis(xAxis: D3ChartSvgg, label: string, transform?: string): void {
    const classes = 'fill-foreground-primary-60 text-xs font-semibold';

    const text = xAxis
      .append('g')
      .attr('x', 0)
      .attr('y', 0)
      .append('text')
      .attr('class', classes)
      .attr('transform', transform ?? `translate(-20, 17)`);

    text.append('tspan').attr('x', 0).attr('y', 0).attr('dx', 0).attr('dy', 0).attr('text-anchor', 'end').text(label);
  }
}
